import {Component, Input, OnDestroy, OnInit} from '@angular/core';
import {FormsModule} from "@angular/forms";
import {TextareaModule} from "primeng/textarea";
import {CommonModule} from "@angular/common";
import {ButtonModule} from "primeng/button";
import {MessageModule} from "primeng/message";
import {FloatLabelModule} from "primeng/floatlabel";
import {ProgressSpinnerModule} from "primeng/progressspinner";
import {Subject, Subscription, takeUntil} from "rxjs";
import {EbayService} from "@app/core/services";
import {ConfirmationService, MessageService, PrimeIcons} from "primeng/api";
import {DialogService, DynamicDialogRef} from "primeng/dynamicdialog";
import {
    EbayMarketplaceOffer,
    EbayMarketplaceResetTraductionResult,
    EbayStock
} from "@app/core/interfaces/ebay";
import {TooltipModule} from "primeng/tooltip";
import {ConfirmDialogModule} from "primeng/confirmdialog";

@Component({
  selector: 'app-product-detail-marketplace-form',
  imports: [
      CommonModule,
      ButtonModule,
      FormsModule,
      TextareaModule,
      FloatLabelModule,
      MessageModule,
      ProgressSpinnerModule,
      TooltipModule,
      ConfirmDialogModule
  ],
  providers: [ConfirmationService, DialogService],
  templateUrl: './product-detail-marketplace-form.html'
})
export class ProductDetailMarketplaceForm implements OnDestroy, OnInit {
    @Input() marketplace!: EbayMarketplaceOffer;
    @Input() ebayStock!: EbayStock;

    protected apiCall$?: Subscription;
    private destroy$: Subject<any> = new Subject();

    copyMarketplace!: Partial<EbayMarketplaceOffer>;
    isSendingDatas: boolean = false;
    resettingFields: string[] = [];

    constructor(
        private dialogRef: DynamicDialogRef,
        private messageService: MessageService,
        private confirmationService: ConfirmationService,
        private ebayService: EbayService
    ) {}

    ngOnInit(): void {
        // Initialization logic if needed
        this.copyMarketplace = JSON.parse(JSON.stringify(this.marketplace));
        // console.log("ProductDetailMarketplaceForm initialized with marketplace:", this.marketplace);
        // console.log("ProductDetailMarketplaceForm initialized with ebayStock:", this.ebayStock);
    }

    onReset(input: string | null): void {
        // console.log("Resetting fields based on input:", input);
        this.confirmationService.confirm({
            message: `Êtes-vous sûr de vouloir réinitialiser ${input ? `le champ "${input}"` : 'les champs'}?`,
            header: 'Confirmer la réinitialisation',
            icon: 'pi pi-exclamation-triangle',
            acceptIcon: PrimeIcons.CHECK,
            acceptButtonProps: {
                iconPos: 'right'
            },
            rejectIcon: PrimeIcons.TIMES,
            rejectButtonProps: {
                text: true,
                severity: 'danger'
            },
            accept: () => {
                this.isSendingDatas = true;
                const resetInput = input ? input : 'les_deux';
                this.resettingFields.push(resetInput);
                this.ebayService
                    .postEbayMarketplaceResetTraduction(this.ebayStock.id as string | number, {marketplace_id: this.marketplace.marketplace_id, reset: resetInput, ecraser_manuel: true})
                    .pipe(takeUntil(this.destroy$))
                    .subscribe({
                        next: (result: EbayMarketplaceResetTraductionResult) => {
                            console.log("Reset successful:", result);
                            const updatedMarketplace = result.marketplaces.find((mp) => mp.marketplace_id === this.marketplace.marketplace_id);
                            if (updatedMarketplace) {
                                this.copyMarketplace.titre = updatedMarketplace.titre;
                                this.copyMarketplace.description = updatedMarketplace.description;
                                this.copyMarketplace.titre_manuel = updatedMarketplace.titre_manuel;
                                this.copyMarketplace.description_manuelle = updatedMarketplace.description_manuelle;
                                this.marketplace.titre = updatedMarketplace.titre;
                                this.marketplace.description = updatedMarketplace.description;
                                this.marketplace.titre_manuel = updatedMarketplace.titre_manuel;
                                this.marketplace.description_manuelle = updatedMarketplace.description_manuelle;
                            }
                            this.isSendingDatas = false;
                            this.resettingFields = this.resettingFields.filter(field => field !== resetInput);
                            this.messageService.add({ severity: 'success', summary: 'Succès', detail: 'Traduction réinitialisée' })
                        },
                        error: (error: any) => {
                            console.error("Error during reset:", error);
                            this.isSendingDatas = false;
                            this.resettingFields = this.resettingFields.filter(field => field !== resetInput);
                            this.messageService.add({ severity: 'error', summary: 'Erreur', detail: 'Une erreur est survenue lors de la réinitialisation' })
                        }
                    });
            }
        });
    }

    isResetting(input: string | null): boolean {
        const resetInput = input ? input : 'les_deux';
        return this.resettingFields.includes(resetInput);
    }

    private getChangedFields(): Partial<EbayMarketplaceOffer> {
        const changes: Record<string, unknown> = {};
        const keys = Object.keys(this.copyMarketplace) as Array<keyof EbayMarketplaceOffer>;

        for (const key of keys) {
            const currentValue = this.copyMarketplace[key];
            const originalValue = this.marketplace[key];

            if (JSON.stringify(currentValue) !== JSON.stringify(originalValue)) {
                changes[key as string] = currentValue;
            }
        }

        return changes as Partial<EbayMarketplaceOffer>;
    }

    public hasChanges(): boolean {
        // If creating a new stock (no id), always allow saving
        if (!this.marketplace.marketplace_id) {
            return true;
        }
        // If editing, check if there are any changes
        const changes = this.getChangedFields();
        return Object.keys(changes).length > 0;
    }

    onSubmit(): void {
        this.isSendingDatas = true;
        const changes = this.getChangedFields();
        // console.log("Detected Changes:", changes);

        const data: any = {};

        if(changes.titre) {
            data['titre'] = changes.titre;
            data['titre_manuel'] = true;
        }
        if(changes.description) {
            data['description'] = changes.description;
            data['description_manuelle'] = true;
        }


        this.ebayService.patchEbayMarketplaceTraduction(this.ebayStock.id as string | number, this.marketplace.marketplace_id as string | number, data)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (result) => {
                    console.log("Marketplace updated successfully:", result.result);
                    if(result?.result) {
                        const newData = result.result;
                        this.copyMarketplace.titre = newData.titre;
                        this.copyMarketplace.description = newData.description;
                        this.copyMarketplace.titre_manuel = newData.titre_manuel;
                        this.copyMarketplace.description_manuelle = newData.description_manuelle;
                        this.marketplace.titre = newData.titre;
                        this.marketplace.description = newData.description;
                        this.marketplace.titre_manuel = newData.titre_manuel;
                        this.marketplace.description_manuelle = newData.description_manuelle;
                    }
                    this.messageService.add({ severity: 'success', summary: 'Succès', detail: 'Traduction mis à jour avec succès.' });
                },
                error: (error) => {
                    console.error("Error updating marketplace:", error);
                    this.messageService.add({ severity: 'error', summary: 'Erreur', detail: 'Une erreur est survenue lors de la mise à jour des traductions.' });
                },
                complete: () => {
                    this.isSendingDatas = false;
                    this.dialogRef.close();
                }
            });
    }

    hideDialog(): void {
        this.isSendingDatas = false;
        this.dialogRef.close();
    }

    ngOnDestroy(): void {
        if (this.destroy$) {
            this.destroy$.next(true);
            this.destroy$.complete();
        }
        if(this.apiCall$) {
            this.apiCall$.unsubscribe();
        }
    }
}
